import type Quill from 'quill/core';
import { useCallback, useLayoutEffect, useRef } from 'react';

const EVENTS_FOR_INTERACTION_DETECTION: (keyof WindowEventMap)[] = [
  'wheel',
  'pointerdown',
  'keydown',
];

/**
 * Returns a function that runs a callback for scrolling:
 * 1. If user has already interacted with the page, the callback is run immediately.
 * 2. Otherwise, the callback will monitor the container size and re-run the callback to
 *    ensure the target won't be shifted out of the viewport due to content loading.
 */
const usePerformLoadSafeScrolling = () => {
  const hasUserInteractedRef = useRef(false);
  const activeObserversRef = useRef<Set<ResizeObserver>>(new Set());

  useLayoutEffect(() => {
    const cleanupEventListeners = () => {
      EVENTS_FOR_INTERACTION_DETECTION.forEach((event) => {
        window.removeEventListener(event, onInteract, { capture: true });
      });
    };

    const cleanupObservers = () => {
      activeObserversRef.current.forEach((observer) => observer.disconnect());
      activeObserversRef.current.clear();
    };

    const onInteract = () => {
      if (hasUserInteractedRef.current) return;
      hasUserInteractedRef.current = true;

      cleanupEventListeners();
      cleanupObservers();
    };

    EVENTS_FOR_INTERACTION_DETECTION.forEach((event) => {
      window.addEventListener(event, onInteract, {
        capture: true,
        passive: true,
      });
    });

    return () => {
      cleanupEventListeners();
      cleanupObservers();
    };
  }, []);

  return useCallback(
    (quill: Quill, callback: (hasUserInteracted: boolean) => void) => {
      if (hasUserInteractedRef.current) {
        callback(true);
        return () => {};
      }

      const observer = new ResizeObserver(() => callback(false));
      observer.observe(quill.root);
      activeObserversRef.current.add(observer);

      return () => {
        observer.disconnect();
        activeObserversRef.current.delete(observer);
      };
    },
    [],
  );
};

export default usePerformLoadSafeScrolling;
